import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackLeadConversion } from '../../lib/gtag';
import { getUtm } from '../../lib/utm';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Plane, Ship, X, Send, CheckCircle, CalendarDays } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['січня', 'лютого', 'березня', 'квітня', 'травня', 'червня', 'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня'];

const nextAvia = () => {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  do { d.setDate(d.getDate() + 1); } while (d.getDay() !== 3 || d <= new Date());
  return d;
};

const nextSea = () => {
  const now = new Date();
  const c = [];
  for (let m = 0; m < 2; m++) {
    for (const day of [5, 20]) c.push(new Date(now.getFullYear(), now.getMonth() + m, day, 12, 0, 0));
  }
  return c.find(d => d > now);
};

const fmt = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]}`;
const countdownParts = (d) => {
  const ms = Math.max(0, d - Date.now());
  return {
    days: Math.floor(ms / 86400000),
    hours: Math.floor((ms % 86400000) / 3600000),
    mins: Math.floor((ms % 3600000) / 60000),
    secs: Math.floor((ms % 60000) / 1000),
  };
};

const CounterDigits = ({ value, label }) => {
  const s = String(value).padStart(2, '0');
  return (
    <div className="flex flex-col items-center">
      <div className="flex gap-[2px]">
        {s.split('').map((ch, i) => (
          <span key={i} className="w-[17px] h-[24px] rounded-[4px] bg-[#1a0a00] text-yellow-400 font-mono font-bold text-[13px] flex items-center justify-center border border-black/50 shadow-[inset_0_2px_3px_rgba(0,0,0,0.6),inset_0_-2px_3px_rgba(0,0,0,0.4)] relative overflow-hidden">
            <span className="absolute inset-x-0 top-1/2 h-px bg-black/50" />
            {ch}
          </span>
        ))}
      </div>
      <span className="text-[#3d1a00] text-[9px] font-black uppercase mt-0.5 leading-none">{label}</span>
    </div>
  );
};

export const ShipmentSchedule = () => {
  const [, tick] = useState(0);
  const [cfg, setCfg] = useState(null);
  const [modal, setModal] = useState(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const t = setInterval(() => tick(v => v + 1), 1000);
    axios.get(`${API}/settings/shipment-schedule`).then(r => setCfg(r.data)).catch(() => setCfg({}));
    return () => clearInterval(t);
  }, []);

  const resolve = (key, autoDate, defNote) => {
    const s = cfg?.[key] || {};
    let date = autoDate;
    if (s.date) {
      const d = new Date(`${s.date}T12:00:00`);
      if (!isNaN(d) && d > new Date()) date = d;
    }
    return { enabled: s.enabled !== false, date, note: s.note?.trim() || defNote };
  };

  const avia = resolve('avia', nextAvia(), 'Експрес 7–14 днів');
  const sea = resolve('sea', nextSea(), 'Економ 30–40 днів');
  const cards = [
    { key: 'АВІА', icon: Plane, ...avia },
    { key: 'МОРЕ', icon: Ship, ...sea },
  ].filter(c => c.enabled);

  if (cards.length === 0) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) { toast.error("Введіть ім'я"); return; }
    if (phone.trim().length < 5) { toast.error('Введіть телефон'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: name.trim(), phone: phone.trim(),
        message: `Тип відправки: ${modal.key} — рейс ${fmt(modal.date)}`,
        source: 'schedule', website: '', ...getUtm(),
      });
      trackLeadConversion();
      setSent(true);
      setTimeout(() => { setModal(null); setSent(false); setName(''); setPhone(''); }, 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Помилка надсилання');
    } finally { setSending(false); }
  };

  return (
    <div className="mt-8 animate-fadeIn" style={{ animationDelay: '0.55s' }} data-testid="shipment-schedule">
      <p className="text-slate-400 text-xs font-bold uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
        <CalendarDays className="w-4 h-4 text-yellow-400" /> Графік найближчих відправлень
      </p>
      <div className="flex flex-col sm:flex-row gap-4 max-w-2xl">
        {cards.map(c => (
          <button key={c.key} onClick={() => setModal(c)}
            className="flex-1 group text-left rounded-2xl p-[2px] bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 shadow-[0_0_25px_rgba(251,146,60,0.45)] hover:shadow-[0_0_45px_rgba(251,146,60,0.85)] hover:scale-[1.02] transition-all"
            data-testid={`schedule-card-${c.key === 'АВІА' ? 'avia' : 'sea'}`}>
            <div className="rounded-[14px] bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 px-5 py-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#1a0a00]/90 flex items-center justify-center shrink-0">
                <c.icon className="w-6 h-6 text-yellow-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[#1a0a00] font-black text-lg leading-none uppercase">{c.key} · {fmt(c.date)}</p>
                <div className="flex items-center gap-1.5 mt-2" data-testid={`schedule-countdown-${c.key === 'АВІА' ? 'avia' : 'sea'}`}>
                  {(() => {
                    const p = countdownParts(c.date);
                    return (
                      <>
                        <CounterDigits value={p.days} label="дн" />
                        <span className="text-[#1a0a00] font-black text-sm -mt-2">:</span>
                        <CounterDigits value={p.hours} label="год" />
                        <span className="text-[#1a0a00] font-black text-sm -mt-2">:</span>
                        <CounterDigits value={p.mins} label="хв" />
                        <span className="text-[#1a0a00] font-black text-sm -mt-2">:</span>
                        <CounterDigits value={p.secs} label="сек" />
                      </>
                    );
                  })()}
                </div>
                <p className="text-[#3d1a00] text-xs font-bold mt-1.5">{c.note}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[75] bg-black/75 flex items-center justify-center p-4" onClick={() => setModal(null)} data-testid="schedule-modal">
          <div className="relative w-full max-w-sm rounded-2xl border border-yellow-400/60 bg-[#0a0f1c] shadow-[0_0_55px_-8px_rgba(250,204,21,0.6)] p-6" onClick={e => e.stopPropagation()}>
            <button onClick={() => setModal(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-800/80 border border-slate-700 text-slate-400 hover:text-white flex items-center justify-center" data-testid="schedule-modal-close">
              <X className="w-4 h-4" />
            </button>
            {sent ? (
              <div className="text-center py-6" data-testid="schedule-success">
                <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                <p className="text-white font-bold text-lg">Заявку прийнято!</p>
                <p className="text-slate-400 text-sm mt-1">Менеджер зв'яжеться з вами найближчим часом</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-red-500 flex items-center justify-center">
                    {modal.key === 'АВІА' ? <Plane className="w-5 h-5 text-[#1a0a00]" /> : <Ship className="w-5 h-5 text-[#1a0a00]" />}
                  </div>
                  <div>
                    <p className="text-white font-bold leading-tight">Бронювання: {modal.key}</p>
                    <p className="text-yellow-400 text-xs font-bold">Рейс {fmt(modal.date)} · залишилося {(() => { const p = countdownParts(modal.date); return `${p.days} дн ${p.hours} год`; })()}</p>
                  </div>
                </div>
                <Input placeholder="Ваше ім'я" value={name} onChange={e => setName(e.target.value)} required
                  className="h-11 bg-slate-900/60 border-yellow-400/30 text-white placeholder:text-slate-500 focus:border-yellow-400" data-testid="schedule-name" />
                <Input type="tel" placeholder="+380 ... телефон" value={phone} onChange={e => setPhone(e.target.value)} required
                  className="h-11 bg-slate-900/60 border-yellow-400/30 text-white placeholder:text-slate-500 focus:border-yellow-400" data-testid="schedule-phone" />
                <Button type="submit" disabled={sending}
                  className="w-full h-12 rounded-full border-0 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-[#1a0a00] font-extrabold shadow-[0_0_22px_rgba(251,146,60,0.55)] hover:shadow-[0_0_38px_rgba(251,146,60,0.9)] transition-all"
                  data-testid="schedule-submit">
                  {sending ? 'Відправляю...' : (<><Send className="w-4 h-4 mr-2" />Забронювати місце</>)}
                </Button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
